import { useContext, useEffect } from 'react'

import type {
  GetStaticPaths,
  GetStaticPropsContext,
  GetStaticPropsResult,
  NextPage,
} from 'next'
import { useLocale, useTranslations } from 'next-intl'

import { indexes } from '@mab-common/utils'

import { joinURL } from 'ufo'

import type { ArtworkCardProps } from '~/components/atoms/ArtworkCard/ArtworkCard'
import type { BreadcrumbProps } from '~/components/atoms/Breadcrumb/Breadcrumb'
import Breadcrumb from '~/components/atoms/Breadcrumb/Breadcrumb'
import type { ImageProps } from '~/components/atoms/Image/Image'
import type { KeyMessageBannerProps } from '~/components/atoms/KeyMessageBanner/KeyMessageBanner'
import KeyMessageBanner, {
  ImageAlignmentVariants,
} from '~/components/atoms/KeyMessageBanner/KeyMessageBanner'
import type { PageHeaderProps } from '~/components/atoms/PageHeader/PageHeader'
import PageHeader from '~/components/atoms/PageHeader/PageHeader'
import { ProfileImageType } from '~/components/atoms/ProfileImage/ProfileImage'
import { Text, TextSizes } from '~/components/atoms/Text/Text'
import VerticalPadder from '~/components/atoms/VerticalPadder/VerticalPadder'
import type { AdvantagesBannerProps } from '~/components/blocks/AdvantagesBanner/AdvantagesBanner'
import AdvantagesBanner from '~/components/blocks/AdvantagesBanner/AdvantagesBanner'
import type { AnimatedMessageBannerProps } from '~/components/blocks/AnimatedMessageBanner/AnimatedMessageBanner'
import AnimatedMessageBanner from '~/components/blocks/AnimatedMessageBanner/AnimatedMessageBanner'
import ArtistProfileList from '~/components/blocks/ArtistProfileList/ArtistProfileList'
import type { formatsType } from '~/components/blocks/ArtworksList/ArtworksList'
import ArtworksList from '~/components/blocks/ArtworksList/ArtworksList'
import InPageNav from '~/components/blocks/InPageNav/InPageNav'
import type { NumberedSliderProps } from '~/components/blocks/NumberedSlider/NumberedSlider'
import NumberedSlider from '~/components/blocks/NumberedSlider/NumberedSlider'
import type { TextWithAccordionProps } from '~/components/blocks/TextWithAccordion/TextWithAccordion'
import TextWithAccordion from '~/components/blocks/TextWithAccordion/TextWithAccordion'
import { resolveBlock } from '~/components/serializers'
import { ComponentTheme } from '~/components/themes'
import { BannerTheme } from '~/enums/banner-theme'
import {
  advantagesBanner,
  artworkCard,
  artworkLimiter,
  artworkOrder,
  factList,
  featuredVideo,
  format,
  getBody,
  getDocumentDescription,
  internationalizedTextField,
  link,
  myPortfolioBanner,
  notification,
  overviewBanner,
  reportCard,
} from '~/schemas/common'
import useSearchAnalyst, {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import { onlyPublished, sanityClientCdn, useSanity } from '~/services/sanity'
import { type CollectionChartData } from '~/types/collectionChart'
import { getPageBuilderArtistIndexData } from '~/utils/getBodyArtistIndexData'
import { getInPageNavItemsFromBlocks } from '~/utils/getInPageNavItemsFromBlocks'
import { getRomeoToken } from '~/utils/getRomeoToken'
import { getSEOMetaByType, metaQuery, MetaTemplate } from '~/utils/seo'
import sortObjectsByString from '~/utils/sortObjectsByString'
import uniqueObjects from '~/utils/uniqueObjects'
import type { PageType } from '~/utils/urlResolver'
import { toURL, urlQuery } from '~/utils/urlResolver'

import styles from '~/styles/pages.module.css'
import EsIndexTypes = indexes.EsIndexTypes
import RecordTypes = indexes.RecordTypes

import type { ArticlePreviewCardProps } from '~/components/atoms/ArticlePreviewCard/ArticlePreviewCard'
import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import type { FeaturedVideoProps } from '~/components/atoms/FeaturedVideoSnippet/FeaturedVideoSnippet'
import FeaturedVideoSnippet from '~/components/atoms/FeaturedVideoSnippet/FeaturedVideoSnippet'
import Fixture from '~/components/atoms/Fixture/Fixture'
import CollectionAuctionTable from '~/components/blocks/CollectionAuctionTable/CollectionAuctionTable'
import CollectionValueChart from '~/components/blocks/CollectionValueChart'
import RelatedArticles from '~/components/blocks/RelatedArticles/RelatedArticles'
import type { ReportCardProps } from '~/components/blocks/ReportCard/ReportCard'
import ReportCard from '~/components/blocks/ReportCard/ReportCard'
import ReportsContainer from '~/containers/ReportsContainer/ReportsContainer'
import { SelectedArtworkContext } from '~/contexts/SelectedArtworkContext'
import { CurrentArtworkProps } from '~/contexts/SelectedArtworkReducer'
import { supportedLanguages } from '~/data/lang/settings'
import toPlainText from '~/services/toPlainText'
import type { CollectionAuctionTableData } from '~/types/collectionAuctionTable'
import type { SiteSettings } from '~/types/siteSettings'
import {
  buildInverseTranslationQuery,
  processInverseQueryResults,
} from '~/utils/articles/translationHelpers'
import { buildBreadcrumb, getBreadcrumbJsonLd } from '~/utils/breadcrumb'
import { addTranslations, loadMessages, type Locale } from '~/utils/i18n'
import { PushInteractionLocation } from '~/utils/posthogEvents'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

export interface PageData {
  artistId: string
  collectionId: string
  image: ImageProps
  title: string
  artistSlug: string
  artistImage: ImageProps
  artist: string
  documentDescription: {
    title?: string
    summary?: string
    blocks?: TextWithAccordionProps['data']
    summaryEmbeds?: TextWithAccordionProps['embeds']
  }
  pageHeaderData: PageHeaderProps
  banner: KeyMessageBannerProps
  factList: NumberedSliderProps
  pageSettings: {
    advantagesBanner: AdvantagesBannerProps
  }
  formats: formatsType
  breadcrumb: BreadcrumbProps['list']
  relatedCollections: any
  artistCollections: any
  fallback: ArtworkCardProps[]
  preview: boolean
  queryParams: any
  url: PageType
  body: Array<{
    _type: string
  }>
  overviewBanner?: AnimatedMessageBannerProps
  featuredArticles?: ArticlePreviewCardProps[]
  valueText?: any
  lang: string
  chartData?: CollectionChartData
  tableData?: CollectionAuctionTableData
  relatedReport?: ReportCardProps
  relatedVideo?: FeaturedVideoProps
  featuredReports?: ReportCardProps[]
}

const getQuery = (
  limiter: any,
  scorer: any = '',
  _id?: string,
  lang = 'en'
) => {
  const id = _id ? `'${_id}'` : '^._id'

  return `*[_type == 'collection' && (slug.current == $slug || $slug in _pastSlugs) ${limiter}] ${scorer}[0] {
    image,
    title,
    "artistImage": artist->image,
    "artistSlug": artist->slug.current,
    "artistId": artist->_id,
    "overrideSlugTitle": title,
    ${getDocumentDescription(lang)}
    "relatedCollections": relatedCollections[]-> {
      _id,
      "name": title,
      image,
      ${urlQuery},
      "totalArtworks": count(*[_type == 'artwork' && collection._ref == ${id} && ${artworkLimiter}])
    },
    "artistCollections": *[_type == 'collection' &&
      artist._ref == ^.artist->_id &&
      _id != ${id} &&
      !(_id in ^.relatedCollections[]._ref)
    ][0...10] {
      _id,
      "name": title,
      image,
      ${urlQuery},
      "totalArtworks": count(*[_type == 'artwork' && collection._ref == ^._id && ${artworkLimiter}])
    },
    ${buildInverseTranslationQuery(
      `parent->_id == ^.artist->_id && (!defined(language) || language == $lang) ${onlyPublished}`,
      lang,
      'featuredArticles',
      '_score desc, _createdAt desc',
      8,
      {
        'sellers-guide': 6,
        'buyers-guide': 5,
        'buyer-guide': 4,
        'investment-guide': 3,
        'record-prices': 2,
        'most-famous': 1,
      }
    )},
    "fallback": *[_type == 'artwork' && collection._ref == ${id} && ${artworkLimiter}] | ${artworkOrder} {
      ${artworkCard}
    },
    ${urlQuery},
    ${metaQuery('collection')},
    "collectionId": _id,
    "artist": artist->name,
    "pageHeaderData": {
      title,
      "summary": ${internationalizedTextField('summary')},
    },
    ${myPortfolioBanner}
    "banner": *[_type == 'banner' && showOnHomepage != true && references(${id})] | order(_createdAt desc)[0]{
      title,
      "subtitle": body,
      "images": {
        image
      },
      "textMask": applyOverlay,
      ${link}
    },
    "pageSettings": *[_type == 'settings' ] [0] {
      ${advantagesBanner}
    },
    ${factList(id, lang)},
    ${notification},
    "formats": *[_type == 'artwork' && collection._ref == ${id} && defined(formatDescription) ${limiter}]{
      ${format}
    },
    "valueText": ${internationalizedTextField('valueText')},
    fiveYearChartTitle,
    fiveYearChartSummary,
    ${overviewBanner},
    ${getBody(lang)},
    "relatedReport": relatedReport->{${reportCard}},
    "relatedVideo": relatedVideo->{${featuredVideo}},
    "featuredReports": array::unique(
      *[_type == 'report' && _id == ^._id->relatedReport->_id ${limiter}]{
        ${reportCard}
      }
      + *[_type == 'report' && ^.artist->_id in feedRelatedArtists[]._ref && _id != ^.collection->relatedReport->_id ${limiter}] | order(publishedDate desc){
        ${reportCard}
      }
      + *[_type == 'report' && !defined(feedRelatedArtists) && _id != ^.collection->relatedReport->_id ${limiter}] | order(publishedDate desc) [0...4]{
        ${reportCard}
      }
    )[0...4]
  }`
}

const artworksListEvents = {
  artwork: {
    method: SearchAnalystMethods.clickedObjectIDs,
    name: SearchAnalystEventNames.collectionArtworkClicked,
  },
  artist: {
    method: SearchAnalystMethods.clickedFilters,
    name: SearchAnalystEventNames.collectionArtworkArtistClicked,
  },
}

const Collection: NextPage<PageData> = (props: PageData) => {
  const {
    artistId,
    collectionId,
    title,
    artistSlug,
    artist,
    documentDescription,
    pageHeaderData,
    banner,
    pageSettings,
    factList,
    formats,
    relatedCollections,
    artistCollections,
    overviewBanner,
    body,
    featuredArticles,
    valueText,
    lang,
    chartData,
    tableData,
    relatedReport,
    relatedVideo,
    featuredReports,
  } = props
  const { fallback, breadcrumb, preview } = props

  const locale = useLocale()
  const t = useTranslations('common')
  const tAuctionHistoryTable = useTranslations('auctionHistoryTable')

  // Building up the jump links
  const jumpToLinks = [
    {
      title: t('buy'),
      slug: 'buy',
    },
    {
      title: t('sell'),
      slug: 'sell',
    },
  ]

  if (documentDescription)
    jumpToLinks.push({
      title: documentDescription.title
        ? documentDescription.title
        : t('meaningAnalysis'),
      slug: 'meaning-and-analysis',
    })

  if (valueText)
    jumpToLinks.push({
      title: t('value'),
      slug: 'value-container',
    })

  if (chartData) {
    jumpToLinks.push({
      title: t('auctionResults'),
      slug: 'auction-results',
    })
  }
  if (factList) jumpToLinks.push({ title: t('tenFacts'), slug: '10-facts' })

  const navItems = getInPageNavItemsFromBlocks(body)
  const mergedJumpToLinks = [...jumpToLinks, ...navItems]

  const catalogueLinks = [
    {
      url: toURL({
        slug: artistSlug,
        _type: 'artist',
      }),
      linkText: t('readMoreAbout', { artist }),
    },
  ]

  const { currentArtworkState, currentArtworkDispatch } = useContext(
    SelectedArtworkContext
  )

  useEffect(() => {
    if (artistId && artist && currentArtworkState.artist.id !== artistId) {
      currentArtworkDispatch({
        type: CurrentArtworkProps.UPDATE_SELECTED_ARTWORK,
        payload: {
          artist: {
            id: artistId,
            artist,
          },
        },
      })
    }

    return () => {
      currentArtworkDispatch({
        type: CurrentArtworkProps.UPDATE_SELECTED_ARTWORK,
        payload: null,
      })
    }
  }, [artistId, artist, currentArtworkDispatch, currentArtworkState.artist])

  const { sendSearchInsightsViewEvent } = useSearchAnalyst()

  useEffect(() => {
    sendSearchInsightsViewEvent(SearchAnalystMethods.viewedFilters, {
      eventName: SearchAnalystEventNames.collectionPageViewed,
      indexType: EsIndexTypes.ARTWORKS,
      filters: [`collectionId:${collectionId}`],
    })
  }, [])

  const overrideTitle = `${artist} ${pageHeaderData.title} ${t('forSale')}`

  const combinedCollections = []

  if (relatedCollections && Array.isArray(relatedCollections)) {
    combinedCollections.push(...relatedCollections)
  }

  if (
    combinedCollections.length < 7 &&
    artistCollections &&
    Array.isArray(artistCollections)
  ) {
    const remainingSlots = 7 - combinedCollections.length
    combinedCollections.push(...artistCollections.slice(0, remainingSlots))
  }

  return (
    <>
      <Breadcrumb list={breadcrumb} />
      <PageHeader
        {...pageHeaderData}
        childComponent={
          relatedReport && lang === 'en' ? (
            <ReportCard
              {...relatedReport}
              variant="snippet"
              addBackground
              blockTitle="Latest report"
            />
          ) : (
            relatedVideo && (
              <FeaturedVideoSnippet
                video={relatedVideo}
                showTitle={lang === 'en'}
              />
            )
          )
        }
      />
      <InPageNav
        items={mergedJumpToLinks}
        title={title}
        idAppend=""
        footerComponent={undefined}
      />

      <div id="buy">
        <ArtworksList
          sectionId="buy"
          queryId={collectionId}
          pageSize={40}
          type="collection"
          formats={formats}
          fallbackData={fallback}
          searchPlaceholder={t('searchArtistOrArtwork')}
          searchInsightsEvents={artworksListEvents}
          artistName={artist}
          overrideTitle={overrideTitle}
          noArtistLink
        />
      </div>

      {valueText && (
        <div id="value-container">
          <Fixture verticalPadding noPaddingTop>
            <Container gutters={gutterTypes.always}>
              <Text
                Tag="h3"
                size={TextSizes.primaryTitleLrg}
                className={styles.collectionValueTitle}
              >
                {title} Value (5 Years)
              </Text>
              <div className={styles.collectionValueText}>
                <BlockContent value={valueText}></BlockContent>
              </div>
            </Container>
          </Fixture>
        </div>
      )}

      {chartData && (
        <div id="auction-results">
          <CollectionValueChart
            data={chartData}
            collectionId={collectionId}
            theme={ComponentTheme.secondary}
            title={`${title} ${tAuctionHistoryTable('marketValue')}`}
          />
        </div>
      )}

      {tableData && (
        <div id="collection-value-table">
          <CollectionAuctionTable data={tableData} smallTitle={true} />
        </div>
      )}

      <div id="sell">
        <AdvantagesBanner
          {...pageSettings.advantagesBanner}
          theme={BannerTheme.offwhite}
          searchInsightsEvent={{
            method: SearchAnalystMethods.convertedFilters,
            name: SearchAnalystEventNames.collectionRequestValuationClicked,
          }}
          collectionId={collectionId}
          analyticsInteractionLocation={PushInteractionLocation.CollectionPage}
          analyticsCtaSource="CollectionPage"
        />
      </div>

      {documentDescription.blocks && documentDescription.summary && (
        <div id="meaning-and-analysis">
          <TextWithAccordion
            sectionId="meaning-and-analysis"
            title={
              documentDescription.title
                ? documentDescription.title
                : t('meaningAnalysis')
            }
            data={documentDescription.blocks}
            summary={documentDescription.summary}
            embeds={documentDescription.summaryEmbeds}
            links={catalogueLinks}
          />
        </div>
      )}

      {factList && (
        <NumberedSlider
          {...factList}
          elementId="10-facts"
          theme={ComponentTheme.secondary}
          linkText={t('allFacts', { title })}
          seeMoreButtonText={t('allFacts', { title })}
          key={`${title}-recordprices`}
          searchInsightEvent={{
            method: SearchAnalystMethods.convertedObjectIDs,
            name: SearchAnalystEventNames.collectionRelatedArticleClicked,
          }}
        />
      )}

      {body && lang == 'en' && body.map((block, i) => resolveBlock(block, i))}

      {/* {relatedCollections && relatedCollections.length > 0 && (
        <VerticalPadder noPaddingBottom={true}>
          <ArtistProfileList
            artists={relatedCollections}
            totalArtists={relatedCollections.length}
            type={ProfileImageType.Artwork}
            allBtnHandler={() => false}
            hideViewAll={true}
            title={t('notableCollections')}
            showNumberOfWorks={true}
            searchInsightsEvent={{
              method: SearchAnalystMethods.clickedFilters,
              name: SearchAnalystEventNames.collectionRelatedCollectionClicked,
            }}
            recordType={RecordTypes.COLLECTION}
          />
        </VerticalPadder>
      )} */}
      {combinedCollections && combinedCollections.length > 0 && (
        <VerticalPadder>
          <ArtistProfileList
            artists={combinedCollections}
            totalArtists={combinedCollections.length}
            type={ProfileImageType.Artwork}
            allBtnHandler={() => false}
            hideViewAll={true}
            title={t('notableCollections')}
            showNumberOfWorks={true}
            searchInsightsEvent={{
              method: SearchAnalystMethods.clickedFilters,
              name: SearchAnalystEventNames.collectionRelatedCollectionClicked,
            }}
            recordType={RecordTypes.COLLECTION}
          />
        </VerticalPadder>
      )}

      {banner && banner?.images && (
        <KeyMessageBanner
          {...banner}
          imageAlign={ImageAlignmentVariants.full}
        />
      )}

      {featuredReports && locale == 'en' && (
        <ReportsContainer
          reports={featuredReports}
          title={`Featured reports`}
          slider
          consise
          theme={ComponentTheme.secondary}
          viewAllLink
        />
      )}

      {featuredArticles && featuredArticles.length > 0 && (
        <div id="articles" key={`featured-collection`}>
          <RelatedArticles
            articles={featuredArticles}
            theme={ComponentTheme.primary}
            title={t('featuredArticles')}
            searchInsightsEvent={{
              method: SearchAnalystMethods.clickedObjectIDs,
              name: SearchAnalystEventNames.artistArticleClicked,
            }}
          />
        </div>
      )}

      {overviewBanner && <AnimatedMessageBanner {...overviewBanner} />}
    </>
  )
}

const getStaticPropsOriginal = async function getStaticProps(
  context: GetStaticPropsContext,
  localizedData: {
    settings: any
    allArtists: any
    totalArtists: number
    seoTemplates: any
  }
): Promise<GetStaticPropsResult<any>> {
  try {
    if (!context?.params?.collection) throw new Error('No collection slug')

    const slug = context.params.collection
    const lang = (
      Array.isArray(context?.params?.lang)
        ? context.params.lang[0]
        : context?.params?.lang || 'en'
    ) as Locale
    const messages = await loadMessages(lang)

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { client, limiter, scorer } = await useSanity()

    const data = await client.fetch(
      getQuery(limiter, scorer, undefined, lang),
      {
        slug: slug,
        lang: lang,
      }
    )

    if (!data) throw new Error('No data returned')

    // Post-process featured articles for translation extraction
    if (data.featuredArticles) {
      data.featuredArticles = processInverseQueryResults(
        data.featuredArticles,
        lang
      )
    }

    data.body = data.body ? await getPageBuilderArtistIndexData(data) : []

    const faqSchema = data.documentDescription.blocks
      ? data.documentDescription.blocks.filter(
          (item: any) => item.faqSchema !== false
        )
      : []

    const breadcrumb: BreadcrumbProps['list'] | undefined = buildBreadcrumb(
      [`artist-${context.params.slug as string}`, data.title],
      '',
      undefined,
      messages,
      lang
    )
    const breadcrumbJsonLd = breadcrumb ? getBreadcrumbJsonLd(breadcrumb) : []

    const meta = await getSEOMetaByType(
      MetaTemplate.Collection,
      data,
      localizedData.settings as unknown as SiteSettings,
      undefined,
      lang
    )

    const notFound = Object.keys(data).length < 1
    let chartData = null
    let tableData = null
    if (process.env.ROMEO_VAULT_URL) {
      const romeoBaseUrl = process.env.ROMEO_VAULT_URL
      const apiUsername = process.env.ROMEO_USER
      const apiPassword = process.env.ROMEO_PASSWORD

      if (apiUsername && apiPassword) {
        try {
          console.log('Fetching Romeo auth token for collection value chart...')

          // Reuse a cached Romeo token where possible to avoid minting a fresh
          // token for every collection page during a build.
          const access = await getRomeoToken({
            baseUrl: romeoBaseUrl,
            username: apiUsername,
            password: apiPassword,
          })
          const headers = {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${access}`,
          }
          console.log(
            'Romeo auth successful, fetching auction data for collection...'
          )

          {
            try {
              // Chart data fetch - default to 10 years
              const currentYear = new Date().getFullYear()
              const chartQueryParams = new URLSearchParams({
                period: 'yearly',
                mny: String(currentYear - 10),
              })
              const chartApiUrl = `${process.env.ROMEO_VAULT_URL}/report/api/v3/collection-market-performance/${data.collectionId}?${chartQueryParams.toString()}`
              const collectionChartReq = await fetch(chartApiUrl, { headers })
              if (collectionChartReq.ok) {
                chartData = await collectionChartReq.json()
              } else {
                console.error('No chart data available for collection')
              }

              // Auction table fetch
              const tableApiUrl = `${process.env.ROMEO_VAULT_URL}/report/api/v3.1/collection-recent-sales/${data.collectionId}`
              const tableReq = await fetch(tableApiUrl, { headers })
              if (tableReq.ok) {
                tableData = await tableReq.json()

                // Enrich sales data with Sanity artwork images and details
                if (tableData && tableData.sales && data.fallback) {
                  tableData.sales = tableData.sales.map((sale: any) => {
                    const matchingArtwork = data.fallback.find(
                      (artwork: any) => {
                        const sanityNumericId = Number(
                          artwork._id.replace('aw-', '')
                        )
                        // Romeo uses plain numeric artwork_id
                        return sanityNumericId === sale.artwork_id
                      }
                    )
                    return {
                      ...sale,
                      image: matchingArtwork?.image || null,
                      format: matchingArtwork?.format || null,
                      artist: matchingArtwork?.artist || null,
                      signed: matchingArtwork?.signed || null,
                      listPriceMin: matchingArtwork?.listPriceMin || null,
                      listPriceMax: matchingArtwork?.listPriceMax || null,
                      listPriceMinValues:
                        matchingArtwork?.listPriceMinValues || null,
                      listPriceMaxValues:
                        matchingArtwork?.listPriceMaxValues || null,
                      artistSlug: matchingArtwork?.artistSlug || null,
                      artworkSlug: matchingArtwork?.artworkSlug || null,
                      collectionSlug: matchingArtwork?.collectionSlug || null,
                      collectionTaxonomy:
                        matchingArtwork?.collectionTaxonomy || null,
                    }
                  })
                }
              } else {
                console.error('No chart data available for collection')
              }
            } catch (error) {
              console.error(
                'Failed to fetch collection auction data from Romeo:',
                error
              )
            }
          }
        } catch (error) {
          console.error('Error with Romeo authentication:', error)
        }
      } else {
        console.warn(
          'Romeo credentials not configured, skipping value chart data'
        )
      }
    }

    return {
      props: {
        notFound: notFound,
        ...data,
        chartData,
        tableData,
        meta,
        lang,
        smallHeader: true,
        formats: sortObjectsByString(uniqueObjects(data.formats), 'name'),
        queryParams: { slug: slug },
        preview: !!context.preview,
        breadcrumb,
        breadcrumbJsonLd,
        jsonLd: {
          breadcrumbJsonLd: true,
          collectionJsonLd: {
            name: data?.pageHeaderData?.title || '',
            description:
              typeof data.pageHeaderData.summary === 'string'
                ? data.pageHeaderData.summary
                : toPlainText(
                    data.pageHeaderData.summary ??
                      data.pageHeaderData.summary?.slice(1, 2)
                  ),
            artworks: data.fallback || [],
            baseUrl: process.env.NEXT_PUBLIC_SITE_URL,
            artistUrl: joinURL(
              process.env.NEXT_PUBLIC_SITE_URL || '',
              lang === 'en' ? '' : lang,
              `artist-${data.url.artist}`
            ),
            target: {
              cssSelector: ['.json-ld-speakable'], // need to add the css selector to the relevant component on this page
            },
          },
          faqJsonLd: [...faqSchema],
        },
        hasGoogleReviews: true,
      },
    }
  } catch (e) {
    console.log('Collection page error:', e)
    // 301 redirect to artist page if collection does not exist
    if (context?.params?.slug) {
      return {
        props: {},
        redirect: {
          permanent: true,
          destination: toURL({
            _type: 'artist',
            slug: context?.params?.slug as string,
          }),
        },
      }
    }
    return {
      notFound: true,
    }
  }
}

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(
  async (context, localizedData) => {
    const result = await getStaticPropsOriginal(context, localizedData)

    // Check if it's a redirect or notFound result - same pattern as artwork page
    if ('redirect' in result || 'notFound' in result) {
      return result
    }

    if ('props' in result) {
      return {
        props: await addTranslations(result.props, context),
      }
    }

    return result
  }
)

export const getStaticPaths: GetStaticPaths = async () => {
  const limiter =
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF === 'main'
      ? '[0..10]'
      : '[0..10]'

  const pages = await sanityClientCdn.fetch<Array<{ url: PageType }>>(
    `*[(_type in $pageTypes && _type != 'artist' && defined(slug.current) && (!defined(isVisible) || isVisible == true) && !(_id in path('drafts.**')))]
        | score(
        boost(_type match "collection", 4))
        | order(_score desc)
        ${limiter} {
          ${urlQuery}
    }`,
    {
      pageTypes: ['collection'],
    }
  )

  const paths = (pages?.length ? pages : [])
    .filter(p => !['/', '404', 'artworks', 'search'].includes(p.url.slug))
    .flatMap(p => {
      const artistSlug = String(p.url.artist || '')
      const collectionSlug = String(p.url.slug || '')
      const taxonomy = String(p.url.collectionTaxonomy || 'collection')

      if (!artistSlug || !collectionSlug || !taxonomy) {
        console.log('Missing required params:', {
          artistSlug,
          collectionSlug,
          taxonomy,
        })
        return []
      }

      return ['en', ...supportedLanguages].map(lang => ({
        params: {
          lang,
          slug: artistSlug,
          collectionTaxonomy: taxonomy,
          collection: collectionSlug,
        },
      }))
    })
    .filter(
      path =>
        path.params.slug &&
        path.params.collectionTaxonomy &&
        path.params.collection
    )

  return {
    paths,
    fallback: 'blocking',
  }
}

export default Collection
